/*
 * Quartz Design System — Primitive Color Tokens
 * Source: Quartz v3.0 2026
 * Generated: 2026-05-25
 */

/**
 * Recursively deep-freeze an object so that no nested
 * property can be reassigned or mutated at runtime.
 *
 * @param {Object} obj - The object to deep-freeze.
 * @return {Object} The same object, now deeply frozen.
 */
export const deepFreeze = obj => {
    Object.freeze(obj);
    Object.keys(obj).forEach(key => {
        if (typeof obj[key] === 'object' && obj[key] !== null && !Object.isFrozen(obj[key])) {
            deepFreeze(obj[key]);
        }
    });
    return obj;
};

export const PRIMITIVE_COLORS = deepFreeze({
    brand: {
        orange: {
            50: '#FEEFE9',
            100: '#FBCCBC',
            200: '#F9B39B',
            300: '#F6906E',
            400: '#F47B51',
            500: '#F15A26',
            600: '#DB5223',
            700: '#AB401B',
            800: '#853215',
            900: '#652610',
        },
    },
    primary: {
        aqua: {
            50: '#F5FCFF',
            100: '#E0F5FF',
            200: '#D1F0FF',
            300: '#BDE9FF',
            400: '#B0E5FF',
            500: '#9CDEFF',
            600: '#8ECAE8',
            700: '#5BACCF',
            800: '#3A8AAD',
            900: '#1F6A8A',
        },
    },
    secondary: {
        tan: {
            50: '#FDFCFB',
            100: '#FAF6F3',
            200: '#F8F2EE',
            300: '#F4ECE6',
            400: '#F2E9E1',
            500: '#EFE3D9',
            600: '#D9CFC5',
            700: '#AAA19A',
            800: '#837D77',
            900: '#645F5B',
        },
    },
    neutral: {
        white: '#FFFFFF',
        black: '#282828',
        grey: {
            50: '#F1F2F2',
            100: '#E6E7E8',
            200: '#CDCED0',
            300: '#BCBEC0',
            400: '#A9AAAD',
            500: '#939598',
            600: '#808285',
            700: '#6D6E71',
            800: '#58595B',
            900: '#3E3F40',
        },
        graphite: {
            50: '#F0F1F3',
            100: '#CFD5D9',
            200: '#B8C0C7',
            300: '#97A4AD',
            400: '#83929D',
            500: '#647785',
            600: '#5B6C79',
            700: '#47545E',
            800: '#374149',
            900: '#2A3238',
        },
    },
    semantic: {
        red: {
            50: '#F8EBED',
            100: '#E8C0C7',
            200: '#DDA1AB',
            300: '#CD7685',
            400: '#C45C6D',
            500: '#B53349',
            600: '#A52E42',
            700: '#812434',
            800: '#641C28',
            900: '#4C151F',
        },
        green: {
            50: '#EAF2EE',
            100: '#BCD8CA',
            200: '#9CC5B0',
            300: '#6FAA8C',
            400: '#539976',
            500: '#288054',
            600: '#24744C',
            700: '#1C5B3C',
            800: '#16462E',
            900: '#113623',
        },
        yellow: {
            50: '#FFF9EB',
            100: '#FDEBC2',
            200: '#FDE2A5',
            300: '#FCD57C',
            400: '#FBCD62',
            500: '#FAC03B',
            600: '#E4AF36',
            700: '#B2882A',
            800: '#8A6A20',
            900: '#695119',
        },
        blue: {
            50: '#F1F6FC',
            100: '#D2E4F7',
            200: '#BCD7F3',
            300: '#9EC5ED',
            400: '#8BBAE9',
            500: '#6EA9E4',
            600: '#649ACF',
            700: '#4E78A2',
            800: '#3D5D7D',
            900: '#2E4760',
        },
    },
    accent: {
        purple: {
            50: '#F2EEF6',
            100: '#D8CCE3',
            200: '#C5B3D6',
            300: '#AB90C3',
            400: '#9A7AB7',
            500: '#8159A5',
            600: '#755196',
            700: '#5C3F75',
            800: '#47315B',
            900: '#362545',
        },
        cobalt: {
            50: '#EAEEFB',
            100: '#BFC9F4',
            200: '#A0AFEE',
            300: '#748BE6',
            400: '#5975E1',
            500: '#3052DA',
            600: '#2C4BC6',
            700: '#223A9B',
            800: '#1A2D78',
            900: '#14225C',
        },
    },
    overlay: {
        subtle: {
            rgba: 'rgba(0, 0, 0, 0.32)',
        },
        default: {
            rgba: 'rgba(0, 0, 0, 0.50)',
        },
        heavy: {
            rgba: 'rgba(0, 0, 0, 0.72)',
        },
    },
});
